/* =====================================================================
 * Runtime font loader — tenant-aware (Phase 8).
 *
 * Reads the resolved tenant snapshot (`@auw/onprem`) and loads the
 * UI fonts declared in `tenant.assets.fonts`. Missing values mean
 * "do not inject anything" — the host SPA is expected to bundle its
 * own fonts via `import` (Vite emits + content-hashes them) so the
 * dist is self-contained.
 *
 * Historically the defaults pointed at jsdelivr / Google Fonts so
 * tests and the early B2C shell could render without configuration,
 * but that pattern leaks fetches in air-gapped on-prem deployments
 * (CSP `style-src 'self'` blocks them, leaving the stylesheet load
 * dangling). Empty defaults make the leak impossible by construction.
 * ===================================================================== */

import { getTenantSnapshot } from '@auw/onprem';
import { resolveTenantAssetFonts } from '@auw/tenant';

const PRETENDARD_STYLESHEET_ID = 'runtime-font-pretendard';
const JETBRAINS_MONO_STYLESHEET_ID = 'runtime-font-jetbrains-mono';

const DEFAULT_BODY_HREF = '';

const DEFAULT_MONO_HREF = '';

function appendStylesheetOnce(id: string, href: string) {
  if (typeof document === 'undefined') return;
  if (href.trim().length === 0) return;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

function afterFirstPaint(callback: () => void) {
  if (typeof window === 'undefined') return;

  window.requestAnimationFrame(() => {
    window.setTimeout(callback, 0);
  });
}

export function scheduleRuntimeFontLoading() {
  if (typeof window === 'undefined') return;

  const tenant = getTenantSnapshot();
  const { bodyHref, monospaceHref } = tenant
    ? resolveTenantAssetFonts(tenant, {
        bodyHref: DEFAULT_BODY_HREF,
        monospaceHref: DEFAULT_MONO_HREF,
      })
    : { bodyHref: DEFAULT_BODY_HREF, monospaceHref: DEFAULT_MONO_HREF };

  if (bodyHref) {
    // 본문 폰트는 첫 페인트 직후 바로 로드해 FOUT는 짧게, 렌더 차단은 피한다.
    afterFirstPaint(() => {
      appendStylesheetOnce(PRETENDARD_STYLESHEET_ID, bodyHref);
    });
  }

  if (!monospaceHref) return;
  const loadMonoFont = () => {
    appendStylesheetOnce(JETBRAINS_MONO_STYLESHEET_ID, monospaceHref);
  };

  // 코드 폰트는 초기 화면에 필수는 아니라 idle 타이밍까지 미룬다.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(loadMonoFont, { timeout: 1800 });
    return;
  }

  window.setTimeout(loadMonoFont, 1200);
}
